import { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loading, Empty } from "../components/kit";
import PeriodBar from "../components/PeriodBar";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PlanDialog, ReenrollDialog, UpsellDialog, StaffPicker } from "../components/gms/GmsForms";
import { GmsCards, GmsMetricTable, GmsBreakdown } from "../components/gms/GmsSummary";
import GmsPayments from "../components/gms/GmsPayments";
import GmsEntries from "../components/gms/GmsEntries";

function Filters({ f, setF }) {
  const { user, stores } = useAuth();
  const [managers, setManagers] = useState([]);
  useEffect(() => { if (user.role === "admin") api.get("/users", { params: { role: "manager" } }).then((r) => setManagers(r.data)); }, [user.role]);
  return (
    <div className="flex flex-wrap gap-2 mb-6" data-testid="gms-filters">
      {user.role === "admin" && (
        <>
          <Select value={f.store_id || "all"} onValueChange={(v) => setF({ ...f, store_id: v === "all" ? "" : v, staff_id: "" })}>
            <SelectTrigger className="w-48 h-11" data-testid="gms-store-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All stores</SelectItem>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.manager_id || "all"} onValueChange={(v) => setF({ ...f, manager_id: v === "all" ? "" : v })}>
            <SelectTrigger className="w-48 h-11" data-testid="gms-manager-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All managers</SelectItem>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
        </>
      )}
      <div className="w-56"><StaffPicker value={f.staff_id} onChange={(v) => setF({ ...f, staff_id: v })} storeId={f.store_id} allLabel="All staff" testid="gms-staff-filter" /></div>
    </div>
  );
}

export default function Gms() {
  const { user, range, storeId } = useAuth();
  const staff = user.role === "staff";
  const [f, setF] = useState({ store_id: user.role === "manager" ? storeId : "", staff_id: "", manager_id: "" });
  const filters = useMemo(() => Object.fromEntries(Object.entries(f).filter(([, v]) => v)), [f]);
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [dlg, setDlg] = useState(null);
  const [key, setKey] = useState(0);

  useEffect(() => {
    if (!range) return;
    api.get("/gms/summary", { params: { start: range.start, end: range.end, ...filters } }).then((r) => setS(r.data)).catch((e) => setErr(errMsg(e)));
  }, [range, filters, key]);

  const done = () => { setDlg(null); setKey((k) => k + 1); };
  if (err) return <Empty>{err}</Empty>;
  return (
    <div className="bs-rise">
      <div className="mb-6">
        <div className="bs-eyebrow">Gold Mine · Payment tracker</div>
        <h1 className="font-display text-4xl font-extrabold text-[#011E38] tracking-tight mt-1" data-testid="gms-title">{staff ? "MY GMS" : "GMS Tracker"}</h1>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-6" data-testid="gms-actions">
        {[["plan", "GMS Plan"], ["reenroll", "Re-enrollment"], ["upsell", "Upselling"]].map(([k, l]) => (
          <Button key={k} onClick={() => setDlg(k)} className="h-14 rounded-2xl bg-[#011E38] hover:bg-[#032C52] text-sm sm:text-base whitespace-normal leading-tight" data-testid={`gms-add-${k}`}>
            <Plus className="w-4 h-4 mr-1 shrink-0 text-[#C5A059]" /> {l}
          </Button>
        ))}
      </div>
      <PeriodBar hideBasis />
      {!staff && <Filters f={f} setF={setF} />}
      {!s ? <Loading /> : (
        <div className="space-y-8">
          {!staff && <GmsMetricTable s={s} />}
          <GmsCards s={s} />
          <Tabs defaultValue="payments">
            <TabsList className="w-full sm:w-auto">
              <TabsTrigger value="payments" data-testid="gms-tab-payments">{staff ? "My Upcoming Payments" : "Payments"}</TabsTrigger>
              <TabsTrigger value="reenroll" data-testid="gms-tab-reenroll">Re-enrollments</TabsTrigger>
              <TabsTrigger value="upsell" data-testid="gms-tab-upsell">Upselling</TabsTrigger>
              {!staff && <TabsTrigger value="staff" data-testid="gms-tab-staff">By Staff</TabsTrigger>}
              {s.by_store && <TabsTrigger value="stores" data-testid="gms-tab-stores">By Store</TabsTrigger>}
            </TabsList>
            <TabsContent value="payments" className="mt-5"><GmsPayments filters={filters} showStaff={!staff} reloadKey={key} onChanged={() => setKey((k) => k + 1)} /></TabsContent>
            <TabsContent value="reenroll" className="mt-5"><GmsEntries kind="reenroll" filters={filters} range={range} showStaff={!staff} reloadKey={key} /></TabsContent>
            <TabsContent value="upsell" className="mt-5"><GmsEntries kind="upsell" filters={filters} range={range} showStaff={!staff} reloadKey={key} /></TabsContent>
            {!staff && <TabsContent value="staff" className="mt-5"><GmsBreakdown rows={s.by_staff} label="Staff" testid="gms-by-staff" /></TabsContent>}
            {s.by_store && <TabsContent value="stores" className="mt-5"><GmsBreakdown rows={s.by_store} label="Store" testid="gms-by-store" /></TabsContent>}
          </Tabs>
        </div>
      )}
      {dlg === "plan" && <PlanDialog open onClose={() => setDlg(null)} onDone={done} />}
      {dlg === "reenroll" && <ReenrollDialog open onClose={() => setDlg(null)} onDone={done} />}
      {dlg === "upsell" && <UpsellDialog open onClose={() => setDlg(null)} onDone={done} />}
    </div>
  );
}
